import { type FC, useState, useEffect } from "react";
import { Table, Spinner, TableHead, TableHeadCell, TableBody, TableRow, TableCell } from "flowbite-react";
import { getContractsByCustomerId } from "../../services/contracts";
import { formatUtcDateTimeString } from "../../utils/dateFormatter";

interface Contract {
  id: number;
  contract_number: string;
  date_signed: string;
  created_at: string;
  status: string;
  contract_type: {
    id: number;
    type: string;
    company: string;
    category: string;
    description: string;
  };
}

interface CustomerContractsTableProps {
  customerId: number;
}

const CustomerContractsTable: FC<CustomerContractsTableProps> = ({
  customerId,
}) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getContractsByCustomerId(customerId);
        setContracts(data);
      } catch (err) {
        console.error("Error fetching contracts:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Fehler beim Abrufen der Produkte"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [customerId]);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">
        <p>{error}</p>
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        <p>Keine Produkte für diesen Kunden gefunden.</p>
      </div>
    );
  }

  const translateStatus = (status: string) => {
    switch (status) {
      case "Aktiv":
        return "Aktiv";
      case "Unterschrieben":
        return "Unterschrieben";
      case "Storniert":
        return "Storniert";
      default:
        return status;
    }
  };

  return (
    <Table hoverable>
      <TableHead>
        <TableHeadCell>Vertragsnummer</TableHeadCell>
        <TableHeadCell>Typ</TableHeadCell>
        <TableHeadCell>Unternehmen</TableHeadCell>
        <TableHeadCell>Kategorie</TableHeadCell>
        <TableHeadCell>Status</TableHeadCell>
        <TableHeadCell>Unterschrieben am</TableHeadCell>
      </TableHead>
      <TableBody>
        {contracts.map((contract) => (
          <TableRow key={contract.id}>
            <TableCell className="font-medium">
              {contract.contract_number}
            </TableCell>
            <TableCell>{contract.contract_type.type}</TableCell>
            <TableCell>{contract.contract_type.company}</TableCell>
            <TableCell>{contract.contract_type.category}</TableCell>
            <TableCell>
              <span
                className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                  contract.status === "Aktiv"
                    ? "bg-green-100 text-green-800"
                    : contract.status === "Unterschrieben"
                    ? "bg-blue-100 text-blue-800"
                    : contract.status === "Storniert"
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {translateStatus(contract.status)}
              </span>
            </TableCell>
            <TableCell>
              {formatUtcDateTimeString(contract.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default CustomerContractsTable;
